import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Span } from '@opentelemetry/api';
import { X } from 'lucide-react';
import analytics from '../lib/tracking';
import { useSessions } from '../hooks/useSessions';
import { startAudioReadyJourney, tracer } from '../lib/telemetry';

export default function HomePage() {
  const navigate = useNavigate();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const { sessions, loading, error, refetch } = useSessions();

  // journey.home_loaded: from HomePage mount → catalog visible to the user.
  // We start the span on mount, then end it when SessionsContext finishes
  // loading and we have a non-empty catalog. If the user navigates away
  // first, the unmount cleanup ends the span as 'aborted'.
  const homeLoadedSpan = useRef<Span | null>(null);
  useEffect(() => {
    homeLoadedSpan.current = tracer.startSpan('journey.home_loaded', {
      attributes: { page: 'home' },
    });
    return () => {
      if (homeLoadedSpan.current) {
        homeLoadedSpan.current.setAttribute('home_loaded.outcome', 'aborted');
        homeLoadedSpan.current.end();
        homeLoadedSpan.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!homeLoadedSpan.current) return;
    if (loading) return;
    const categories = Object.keys(sessions);
    if (categories.length === 0) return;
    const sessionCount = Object.values(sessions).reduce(
      (sum, list) => sum + list.length,
      0
    );
    homeLoadedSpan.current.setAttribute(
      'home_loaded.outcome',
      error ? 'stale' : 'ready'
    );
    homeLoadedSpan.current.setAttribute('catalog.categories', categories.length);
    homeLoadedSpan.current.setAttribute('catalog.sessions', sessionCount);
    homeLoadedSpan.current.end();
    homeLoadedSpan.current = null;
  }, [loading, sessions, error]);

  if (loading && Object.keys(sessions).length === 0) {
    return (
      <div
        className="px-4 pt-6 pb-20"
        data-testid="home-page"
        data-loading="true"
      >
        <p className="text-text-medium text-center mt-12">Loading...</p>
      </div>
    );
  }

  if (error && Object.keys(sessions).length === 0) {
    return (
      <div
        className="px-4 pt-6 pb-20"
        data-testid="home-page"
        data-error="true"
      >
        <p className="text-text-medium text-center mt-12">
          Couldn't load sessions. Please refresh the page.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-20" data-testid="home-page">
      {error && (
        <div
          className="mb-6 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800"
          data-testid="sessions-stale-error"
          role="alert"
        >
          <p className="font-medium mb-1">Couldn't refresh your sessions.</p>
          <p className="text-xs leading-relaxed mb-3">
            The list below may be out of date — sessions only available to
            signed-in users may be missing.
          </p>
          <button
            onClick={refetch}
            className="text-xs font-semibold underline"
          >
            Try again
          </button>
        </div>
      )}
      {Object.entries(sessions).map(([category, sessionList]) => (
        <div key={category} className="mb-8">
          <h2 className="text-text-medium text-xs font-semibold mb-4 tracking-wider uppercase pb-2 border-b border-accent-pink">
            {category}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {sessionList.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onClick={() => {
                  const span = tracer.startSpan('session.click', {
                    attributes: {
                      'session.id': session.id,
                      'session.title': session.title,
                      'session.category': category,
                      'session.has_audio': !!session.audioFile,
                      'session.requires_auth': !!session.requiresAuth,
                    },
                  });

                  // Track session click (fire-and-forget)
                  analytics.clickContent(
                    category.toLowerCase(),
                    `session_${session.id}`,
                    session.title,
                    { session_icon: session.icon }
                  );

                  if (session.title === 'Request') {
                    span.setAttribute('session.click.outcome', 'open_request_modal');
                    analytics.engagement('open_request_modal', 'modal');
                    setShowRequestModal(true);
                  } else {
                    span.setAttribute('session.click.outcome', 'navigate');
                    // Open the cross-component "click → audio ready"
                    // journey span if this session actually has audio.
                    // SessionPage ends it on loadedmetadata or error.
                    if (session.audioFile) {
                      startAudioReadyJourney(session.id, {
                        'session.id': session.id,
                        'session.title': session.title,
                        'audio.file': session.audioFile,
                      });
                    }
                    // Navigate to session page
                    navigate(`/session/${session.id}`);
                  }
                  span.end();
                }}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Request Modal */}
      {showRequestModal && (
        <RequestModal
          onClose={() => setShowRequestModal(false)}
        />
      )}
    </div>
  );
}

// Session Card Component
function SessionCard({ session, onClick }: { session: any, onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="relative bg-card-bg rounded-2xl p-6 flex flex-col items-center justify-center h-40 transition-all hover:shadow-lg active:scale-95 border border-accent-pink"
    >
      {/* Icon */}
      <div className="text-4xl mb-3">{session.icon}</div>

      {/* Title */}
      <h3 className="text-text-dark font-medium text-sm">{session.title}</h3>

      {/* Audio Available Indicator */}
      {session.audioFile && (
        <div className="absolute top-3 left-3">
          <div className="w-2 h-2 bg-green-400 rounded-full" title="Audio available" />
        </div>
      )}
    </button>
  );
}

// Request Modal Component
function RequestModal({ onClose }: { onClose: () => void }) {
  const [request, setRequest] = useState('');
  const [hasStartedTyping, setHasStartedTyping] = useState(false);

  const handleSubmit = () => {
    // Track form submission with type-safe API
    analytics.forms.request_session.submit({
      form_text: request,
      request_length: request.length
    });

    // Track conversion with type-safe API
    analytics.conversions.requestSubmission({
      form_text: request,
      request_length: request.length
    });

    alert('Thank you for your request! We\'ll consider it for future audio sessions.');
    setRequest('');
    onClose();
  };

  const handleTextChange = (value: string) => {
    setRequest(value);

    if (!hasStartedTyping && value.length > 0) {
      // Track form start with type-safe API
      analytics.forms.request_session.start();
      setHasStartedTyping(true);
    }
  };

  const handleClose = () => {
    // Track abandonment regardless of whether text was entered
    // This helps understand modal engagement even when users don't type
    analytics.forms.request_session.abandon({
      form_text: request,
      request_length: request.length
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-auto border-2 border-accent-pink">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-text-dark text-xl font-semibold">Request an Experience</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 bg-accent-pink rounded-full flex items-center justify-center transition-transform active:scale-95"
          >
            <X className="w-4 h-4 text-text-dark" />
          </button>
        </div>

        {/* Request Form */}
        <div className="mb-6">
          <div className="text-center mb-4">
            <div className="text-6xl mb-4">💭</div>
            <p className="text-text-medium text-sm leading-relaxed mb-6">
              What's going on - tell Daddy what you need?
            </p>
          </div>

          <textarea
            value={request}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="Describe your audio request..."
            className="w-full bg-pastel-purple bg-opacity-30 text-text-dark rounded-lg p-4 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-primary-pink border border-accent-pink"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!request.trim()}
          className={`w-full font-medium py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors ${
            request.trim()
              ? 'bg-primary-pink hover:bg-pink-400 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Submit Request
        </button>
      </div>
    </div>
  );
}
